import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSenderAddress } from '../../hooks/useSenderAddress';
import { getAdminId } from '../../utils/getAdminId';
import { classifyOrderIssue } from '../../utils/classifyOrderIssue';
import { supabase } from '../../lib/supabase';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  generatePostNLLabel,
  parseNLAddress,
  validatePostalCode,
  downloadLabelPdf,
  printLabelPdf,
  SERVICE_TYPES,
  LABEL_FORMATS,
  WEIGHT_PRESETS,
  getPostNLTrackingUrl,
  getPostalCodeHint,
  getDestinationType,
  getDestinationLabel,
  getAvailableServiceTypes,
  requiresCustomsDeclaration,
  formatPostNLError,
  SUPPORTED_COUNTRIES,
  COUNTRY_NAMES,
  PostNLLabelRequest,
  PostNLLabelResponse,
  PostNLSenderInfo,
  PostNLReceiverInfo,
  PostNLErrorResponse,
} from '../../lib/postnl';
import { X, Package, Truck, Scale, Ruler, FileText, Download, Printer, ExternalLink, CircleCheck as CheckCircle, CircleAlert as AlertCircle, Loader as Loader2, ChevronDown, ChevronUp, Building2, MapPin, Info, Globe, FileWarning, Sparkles } from 'lucide-react';
import { useAiAddress } from '../../hooks/useAiAddress';

interface PostNLLabelModalProps {
  isOpen: boolean;
  onClose: () => void;
  shipment: {
    id: string;
    shipment_number: string;
    customer_name: string;
    customer_email?: string;
    shipping_address: string;
    tracking_code?: string;
    label_url?: string;
    label_generated_at?: string;
    barcode_number?: string;
    items?: Array<{ quantity: number; weight?: number; weight_unit?: string }>;
  };
  onLabelGenerated?: (labelData: PostNLLabelResponse) => void;
}

export const PostNLLabelModal: React.FC<PostNLLabelModalProps> = ({
  isOpen,
  onClose,
  shipment,
  onLabelGenerated,
}) => {
  const { user, token } = useAuth();
  const adminId = getAdminId(user);
  const { senderAddress } = useSenderAddress();
  const { processing: aiProcessing, lastResult: aiResult, aiError, normalizeAddress } = useAiAddress();
  const [aiProcessed, setAiProcessed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<PostNLErrorResponse | null>(null);
  const [success, setSuccess] = useState(false);
  const [labelResult, setLabelResult] = useState<PostNLLabelResponse | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [serviceType, setServiceType] = useState<'standard' | 'signature' | 'insured' | 'evening' | 'sunday'>('standard');
  const [labelFormat, setLabelFormat] = useState<'A4' | 'A6'>('A6');
  const [weightGrams, setWeightGrams] = useState<number>(500);
  const [customWeight, setCustomWeight] = useState<string>('');
  const [useCustomWeight, setUseCustomWeight] = useState(false);

  const [dimensions, setDimensions] = useState({ height: 0, width: 0, depth: 0 });
  const [useDimensions, setUseDimensions] = useState(false);

  const [customsDescription, setCustomsDescription] = useState('');
  const [customsValue, setCustomsValue] = useState<string>('');

  const parsedAddress = parseNLAddress(shipment.shipping_address);
  const [receiverStreet, setReceiverStreet] = useState(parsedAddress.street);
  const [receiverHouseNumber, setReceiverHouseNumber] = useState(parsedAddress.houseNumber);
  const [receiverPostalCode, setReceiverPostalCode] = useState(parsedAddress.postalCode);
  const [receiverCity, setReceiverCity] = useState(parsedAddress.city);
  const [receiverCountry, setReceiverCountry] = useState(parsedAddress.countryCode || 'NL');
  const [receiverHouseNumberExt, setReceiverHouseNumberExt] = useState('');
  const [receiverRegion, setReceiverRegion] = useState('');
  const [receiverEmail, setReceiverEmail] = useState(shipment.customer_email || '');
  const [receiverPhone, setReceiverPhone] = useState('');

  const [senderCompany, setSenderCompany] = useState(user?.company_name || '');
  const [senderStreet, setSenderStreet] = useState('');
  const [senderHouseNumber, setSenderHouseNumber] = useState('');
  const [senderPostalCode, setSenderPostalCode] = useState('');
  const [senderCity, setSenderCity] = useState('');
  const [senderCountry, setSenderCountry] = useState('NL');
  const [senderEmail, setSenderEmail] = useState(user?.company_email || user?.email || '');
  const [senderPhone, setSenderPhone] = useState(user?.company_phone || '');

  const destinationType = useMemo(() => getDestinationType(receiverCountry), [receiverCountry]);
  const destinationLabel = useMemo(() => getDestinationLabel(receiverCountry), [receiverCountry]);
  const availableServices = useMemo(() => getAvailableServiceTypes(receiverCountry), [receiverCountry]);
  const needsCustoms = useMemo(() => requiresCustomsDeclaration(receiverCountry), [receiverCountry]);
  const needsRegion = useMemo(() => ['US', 'CA', 'AU', 'IT', 'BR', 'MX', 'IN', 'CN', 'JP', 'KR'].includes(receiverCountry), [receiverCountry]);

  const receiverPostalHint = useMemo(() => getPostalCodeHint(receiverCountry), [receiverCountry]);
  const senderPostalHint = useMemo(() => getPostalCodeHint(senderCountry), [senderCountry]);

  const receiverPostalValidation = useMemo(
    () => receiverPostalCode ? validatePostalCode(receiverPostalCode, receiverCountry) : null,
    [receiverPostalCode, receiverCountry]
  );

  const senderPostalValidation = useMemo(
    () => senderPostalCode ? validatePostalCode(senderPostalCode, senderCountry) : null,
    [senderPostalCode, senderCountry]
  );

  useEffect(() => {
    if (!availableServices.some(s => s.value === serviceType)) {
      setServiceType('standard');
    }
  }, [receiverCountry, availableServices, serviceType]);

  useEffect(() => {
    if (isOpen && shipment?.items?.length) {
      let totalGrams = 0;
      for (const item of shipment.items) {
        const weight = item.weight ?? 0;
        const gramsPerUnit = item.weight_unit === 'kg' ? weight * 1000 : weight;
        totalGrams += item.quantity * gramsPerUnit;
      }
      if (totalGrams > 0) {
        setWeightGrams(Math.round(totalGrams));
        setUseCustomWeight(false);
        setCustomWeight('');
      }
    }
  }, [isOpen, shipment?.id, shipment?.items]);

  useEffect(() => {
    if (user?.company_address) {
      const parsed = parseNLAddress(user.company_address);
      setSenderStreet(parsed.street);
      setSenderHouseNumber(parsed.houseNumber);
      setSenderPostalCode(parsed.postalCode);
      setSenderCity(parsed.city);
      setSenderCountry(parsed.countryCode || 'NL');
    }
  }, [user?.company_address]);

  useEffect(() => {
    if (isOpen && shipment.shipping_address) {
      const parsed = parseNLAddress(shipment.shipping_address);
      setReceiverStreet(parsed.street);
      setReceiverHouseNumber(parsed.houseNumber);
      setReceiverPostalCode(parsed.postalCode);
      setReceiverCity(parsed.city);
      setReceiverCountry(parsed.countryCode || 'NL');
      setAiProcessed(false);

      normalizeAddress({
        name: shipment.customer_name,
        street: parsed.street,
        houseNumber: parsed.houseNumber,
        postalCode: parsed.postalCode,
        city: parsed.city,
        countryCode: parsed.countryCode || 'NL',
        phone: receiverPhone,
      }, undefined, {
        orderNumber: shipment.shipment_number || undefined,
        source: 'postnl_modal',
      }).then((result) => {
        if (result) {
          const n = result.normalized;
          if (n.street != null) setReceiverStreet(n.street);
          if (n.houseNumber != null) setReceiverHouseNumber(n.houseNumber);
          if (n.houseNumberExt != null) setReceiverHouseNumberExt(n.houseNumberExt);
          if (n.postalCode != null) setReceiverPostalCode(n.postalCode);
          if (n.city != null) setReceiverCity(n.city);
          if (n.countryCode != null) setReceiverCountry(n.countryCode);
          if (n.region != null) setReceiverRegion(n.region);
          if (n.phone != null) setReceiverPhone(n.phone);
          setAiProcessed(true);

          const actionableIssues = result.diagnostics?.issues?.filter(i => i.severity === 'error' || i.severity === 'warning') || [];
          if (actionableIssues.length > 0 && adminId && shipment.shipment_number) {
            const issueDesc = 'AI detected: ' + actionableIssues.map(i => `${i.field}: ${i.message}`).join('; ');
            supabase.rpc('upsert_order_issue', {
              p_admin_id: adminId,
              p_order_reference: shipment.shipment_number,
              p_shopify_order_id: null,
              p_client_name: shipment.customer_name || null,
              p_recipient_address: { name: shipment.customer_name, street: n.street, houseNumber: n.houseNumber, postalCode: n.postalCode, city: n.city, countryCode: n.countryCode, phone: n.phone, region: n.region },
              p_issue_type: actionableIssues.some(i => i.severity === 'error') ? 'address_validation_error' : 'incomplete_address',
              p_issue_description: issueDesc,
              p_error_code: null,
              p_source: 'ai_address_validation',
            }).then(() => {}, () => {});
          }
        }
      });
    }
  }, [isOpen, shipment.shipping_address]);

  if (!isOpen) return null;

  const hasExistingLabel = !!shipment.label_url && !!shipment.barcode_number;

  const isReceiverValid = receiverStreet && receiverHouseNumber && receiverPostalCode && receiverCity &&
    (!receiverPostalValidation || receiverPostalValidation.valid) &&
    (!needsRegion || receiverRegion);
  const isSenderValid = senderCompany && senderStreet && senderHouseNumber && senderPostalCode && senderCity &&
    (!senderPostalValidation || senderPostalValidation.valid);
  const isWeightValid = useCustomWeight ? parseInt(customWeight) > 0 : weightGrams > 0;
  const isCustomsValid = !needsCustoms || (customsDescription.length > 0);

  const canGenerate = isReceiverValid && isSenderValid && isWeightValid && isCustomsValid && !loading;

  const handleGenerateLabel = async () => {
    if (!token) {
      setError('Authentication required');
      return;
    }

    setLoading(true);
    setError(null);
    setErrorDetails(null);
    setSuccess(false);

    const finalWeight = useCustomWeight ? parseInt(customWeight) : weightGrams;

    const senderInfo: PostNLSenderInfo = {
      companyName: senderCompany,
      street: senderStreet,
      houseNumber: senderHouseNumber,
      postalCode: senderPostalCode.replace(/\s/g, '').toUpperCase(),
      city: senderCity,
      countryCode: senderCountry,
      email: senderEmail || undefined,
      phone: senderPhone || undefined,
    };

    const receiverInfo: PostNLReceiverInfo = {
      companyName: shipment.customer_name,
      street: receiverStreet,
      houseNumber: receiverHouseNumber,
      houseNumberExt: receiverHouseNumberExt || undefined,
      postalCode: receiverPostalCode.replace(/\s/g, '').toUpperCase(),
      city: receiverCity,
      countryCode: receiverCountry,
      region: receiverRegion || undefined,
      email: receiverEmail || undefined,
      phone: receiverPhone || undefined,
    };

    const request: PostNLLabelRequest = {
      shipmentId: shipment.id,
      senderInfo,
      receiverInfo,
      options: {
        serviceType,
        weightGrams: finalWeight,
        dimensions: useDimensions && dimensions.height > 0 ? dimensions : undefined,
        labelFormat,
        reference: shipment.shipment_number,
      },
      customs: needsCustoms ? {
        contentDescription: customsDescription,
        declaredValue: customsValue ? parseFloat(customsValue) : undefined,
        currency: 'EUR',
        originCountry: 'NL',
        valueSplit: senderAddress?.valueSplit ?? 1,
      } : undefined,
    };

    try {
      const result = await generatePostNLLabel(request, token);

      if (result.success) {
        setSuccess(true);
        setLabelResult(result);
        onLabelGenerated?.(result);

        if (adminId && shipment.shipment_number) {
          (async () => { try { await supabase.rpc('resolve_order_issue_by_reference', {
            p_admin_id: adminId,
            p_order_reference: shipment.shipment_number,
          }); } catch {} })();
        }
      } else {
        const errorMsg = result.error || 'Failed to generate label';
        setError(errorMsg);
        if (result.errorDetails) {
          setErrorDetails(result.errorDetails);
        }

        if (adminId && shipment.shipment_number) {
          (async () => { try { await supabase.rpc('upsert_order_issue', {
            p_admin_id: adminId,
            p_order_reference: shipment.shipment_number,
            p_shopify_order_id: null,
            p_client_name: shipment.customer_name ?? null,
            p_recipient_address: {
              street: receiverStreet,
              houseNumber: receiverHouseNumber,
              postalCode: receiverPostalCode,
              city: receiverCity,
              countryCode: receiverCountry,
              phone: receiverPhone,
            },
            p_issue_type: classifyOrderIssue(errorMsg),
            p_issue_description: errorMsg,
            p_error_code: null,
            p_source: 'postnl_label_modal',
          }); } catch {} })();
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMsg);

      if (adminId && shipment.shipment_number) {
        (async () => { try { await supabase.rpc('upsert_order_issue', {
          p_admin_id: adminId,
          p_order_reference: shipment.shipment_number,
          p_shopify_order_id: null,
          p_client_name: shipment.customer_name ?? null,
          p_recipient_address: {
            street: receiverStreet,
            houseNumber: receiverHouseNumber,
            postalCode: receiverPostalCode,
            city: receiverCity,
            countryCode: receiverCountry,
          },
          p_issue_type: classifyOrderIssue(errorMsg),
          p_issue_description: errorMsg,
          p_error_code: null,
          p_source: 'postnl_label_modal',
        }); } catch {} })();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (labelResult?.labelPdfBase64) {
      downloadLabelPdf(labelResult.labelPdfBase64, `PostNL-${labelResult.barcode}.pdf`);
    }
  };

  const handlePrint = () => {
    if (labelResult?.labelPdfBase64) {
      printLabelPdf(labelResult.labelPdfBase64);
    }
  };

  const handleTrack = () => {
    const code = labelResult?.trackingCode || shipment.tracking_code || shipment.barcode_number;
    if (code) {
      window.open(getPostNLTrackingUrl(code, receiverPostalCode, receiverCountry), '_blank');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-4 sm:pt-10 px-4">
      <div className="relative w-full max-w-2xl shadow-2xl rounded-2xl bg-white dark:bg-gray-800 animate-scale-in max-h-[90vh] flex flex-col">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
              <Truck className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                PostNL Shipping Label
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {shipment.shipment_number}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-6 overflow-y-auto flex-1">
          {hasExistingLabel && !success && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                    Label Already Generated
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                    Barcode: <span className="font-mono">{shipment.barcode_number}</span>
                  </p>
                  <div className="flex gap-2 mt-3">
                    {shipment.label_url && (
                      <a
                        href={shipment.label_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        Download
                      </a>
                    )}
                    <button
                      onClick={handleTrack}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 text-blue-700 dark:text-blue-300 text-sm font-medium rounded-lg border border-blue-300 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Track
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {success && labelResult && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800 dark:text-green-300">
                    Label Generated Successfully!
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                    Tracking Code: <span className="font-mono font-bold">{labelResult.trackingCode}</span>
                  </p>
                  {labelResult.destinationType && (
                    <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                      Destination: {destinationLabel} | Product Code: {labelResult.productCode}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      onClick={handleDownload}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </button>
                    <button
                      onClick={handlePrint}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 text-green-700 dark:text-green-300 text-sm font-medium rounded-lg border border-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
                    >
                      <Printer className="h-4 w-4" />
                      Print
                    </button>
                    <button
                      onClick={handleTrack}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 text-green-700 dark:text-green-300 text-sm font-medium rounded-lg border border-green-300 dark:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Track Shipment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 dark:text-red-300">
                    Label Generation Failed
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-400 mt-1">{error}</p>
                  {errorDetails?.suggestion && (
                    <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                      <p className="text-xs font-medium text-red-800 dark:text-red-300">
                        Suggestion:
                      </p>
                      <p className="text-xs text-red-700 dark:text-red-400">
                        {errorDetails.suggestion}
                      </p>
                    </div>
                  )}
                  {errorDetails?.postalCodeFormat && (
                    <p className="text-xs text-red-600 dark:text-red-500 mt-2">
                      Expected postal code format: {errorDetails.postalCodeFormat}
                    </p>
                  )}
                  {errorDetails?.field && (
                    <p className="text-xs text-red-500 dark:text-red-500 mt-1">
                      Field: {errorDetails.field}
                    </p>
                  )}
                  {errorDetails?.allErrors && errorDetails.allErrors.length > 1 && (
                    <div className="mt-3 pt-2 border-t border-red-200 dark:border-red-700">
                      <p className="text-xs font-medium text-red-800 dark:text-red-300 mb-1">
                        All validation errors:
                      </p>
                      <ul className="text-xs text-red-700 dark:text-red-400 space-y-1">
                        {errorDetails.allErrors.map((err, idx) => (
                          <li key={idx} className="flex items-start gap-1">
                            <span className="text-red-500">-</span>
                            <span>{err.message}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!success && (
            <>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Receiver Address
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {aiProcessing && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300 animate-pulse">
                        <Sparkles className="h-3 w-3" /> AI processing…
                      </span>
                    )}
                    {aiProcessed && !aiProcessing && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300">
                        <Sparkles className="h-3 w-3" /> AI-processed
                      </span>
                    )}
                    <div className="flex items-center gap-2 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg">
                      <Globe className="h-3.5 w-3.5 text-gray-500" />
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                        {destinationLabel}
                      </span>
                    </div>
                  </div>
                </div>
                {aiResult?.diagnostics?.issues && aiResult.diagnostics.issues.length > 0 && (
                  <div className="mt-2 p-3 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Sparkles className="h-4 w-4 text-violet-700 dark:text-violet-300 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs font-medium text-violet-900 dark:text-violet-200">AI Address Diagnostics:</p>
                        <ul className="mt-1 list-disc list-inside text-xs text-violet-800 dark:text-violet-300 space-y-0.5">
                          {aiResult.diagnostics.issues.map((issue, idx) => (
                            <li key={`ai-issue-${idx}`} className={issue.severity === 'error' ? 'text-red-700 dark:text-red-300 font-medium' : ''}>
                              [{issue.severity}] {issue.field}: {issue.message}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
                {aiError && (
                  <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-xs text-red-700 dark:text-red-300">AI error: {aiError}</p>
                  </div>
                )}
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl space-y-3">
                  <div className="font-medium text-gray-900 dark:text-gray-100">
                    {shipment.customer_name}
                  </div>
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-6">
                      <Input
                        label="Street"
                        value={receiverStreet}
                        onChange={(e) => setReceiverStreet(e.target.value)}
                        placeholder="Street name"
                        required
                        error={!receiverStreet ? 'Required' : undefined}
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        label="House Nr."
                        value={receiverHouseNumber}
                        onChange={(e) => setReceiverHouseNumber(e.target.value)}
                        placeholder="123"
                        required
                        error={!receiverHouseNumber ? 'Required' : undefined}
                      />
                    </div>
                    <div className="col-span-3">
                      <Input
                        label="Ext."
                        value={receiverHouseNumberExt}
                        onChange={(e) => setReceiverHouseNumberExt(e.target.value)}
                        placeholder="A"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Input
                        label="Postal Code"
                        value={receiverPostalCode}
                        onChange={(e) => setReceiverPostalCode(e.target.value)}
                        placeholder={receiverPostalHint?.replace('Format: ', '') || '1234AB'}
                        required
                        error={receiverPostalValidation && !receiverPostalValidation.valid ? 'Invalid format' : undefined}
                      />
                      {receiverPostalHint && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {receiverPostalHint}
                        </p>
                      )}
                    </div>
                    <Input
                      label="City"
                      value={receiverCity}
                      onChange={(e) => setReceiverCity(e.target.value)}
                      placeholder="Amsterdam"
                      required
                      error={!receiverCity ? 'Required' : undefined}
                    />
                  </div>
                  <div className={`grid ${needsRegion ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Country
                      </label>
                      <select
                        value={receiverCountry}
                        onChange={(e) => setReceiverCountry(e.target.value)}
                        className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-colors text-gray-900 dark:text-gray-100"
                      >
                        {SUPPORTED_COUNTRIES.map(country => (
                          <option key={country.code} value={country.code}>
                            {country.name} {country.type === 'non_eu' ? '(Non-EU)' : country.type === 'eu' ? '(EU)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {needsRegion && (
                      <div>
                        <Input
                          label="State / Province"
                          value={receiverRegion}
                          onChange={(e) => setReceiverRegion(e.target.value)}
                          placeholder={receiverCountry === 'US' ? 'NY' : receiverCountry === 'CA' ? 'ON' : receiverCountry === 'AU' ? 'NSW' : 'Region'}
                          required
                          error={needsRegion && !receiverRegion ? 'Required' : undefined}
                        />
                        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                          Required for {COUNTRY_NAMES[receiverCountry] || receiverCountry}
                        </p>
                      </div>
                    )}
                    <Input
                      label="Email (optional)"
                      type="email"
                      value={receiverEmail}
                      onChange={(e) => setReceiverEmail(e.target.value)}
                      placeholder="customer@example.com"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    <Input
                      label="Phone (recommended for non-EU)"
                      type="tel"
                      value={receiverPhone}
                      onChange={(e) => setReceiverPhone(e.target.value)}
                      placeholder="+1 555 123 4567"
                    />
                  </div>
                </div>
              </div>

              {needsCustoms && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                  <div className="flex items-start gap-3">
                    <FileWarning className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                        Customs Declaration Required
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                        Non-EU destinations require customs information for the shipment.
                      </p>
                      <div className="mt-3 space-y-3">
                        <Input
                          label="Contents Description"
                          value={customsDescription}
                          onChange={(e) => setCustomsDescription(e.target.value)}
                          placeholder="e.g., Clothing, Electronics, Books"
                          required
                          error={!customsDescription ? 'Required for non-EU' : undefined}
                        />
                        <Input
                          label="Declared Value (EUR)"
                          type="number"
                          value={customsValue}
                          onChange={(e) => setCustomsValue(e.target.value)}
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Sender Address
                  </h3>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl space-y-3">
                  <Input
                    label="Company Name"
                    value={senderCompany}
                    onChange={(e) => setSenderCompany(e.target.value)}
                    placeholder="Your company name"
                    required
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Street"
                      value={senderStreet}
                      onChange={(e) => setSenderStreet(e.target.value)}
                      placeholder="Street name"
                      required
                    />
                    <Input
                      label="House Nr."
                      value={senderHouseNumber}
                      onChange={(e) => setSenderHouseNumber(e.target.value)}
                      placeholder="123"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Input
                        label="Postal Code"
                        value={senderPostalCode}
                        onChange={(e) => setSenderPostalCode(e.target.value)}
                        placeholder={senderPostalHint?.replace('Format: ', '') || '1234AB'}
                        required
                        error={senderPostalValidation && !senderPostalValidation.valid ? 'Invalid format' : undefined}
                      />
                      {senderPostalHint && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {senderPostalHint}
                        </p>
                      )}
                    </div>
                    <Input
                      label="City"
                      value={senderCity}
                      onChange={(e) => setSenderCity(e.target.value)}
                      placeholder="Amsterdam"
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Email"
                      type="email"
                      value={senderEmail}
                      onChange={(e) => setSenderEmail(e.target.value)}
                      placeholder="your@email.com"
                    />
                    <Input
                      label="Phone"
                      type="tel"
                      value={senderPhone}
                      onChange={(e) => setSenderPhone(e.target.value)}
                      placeholder="+31 6 12345678"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-gray-500" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Package Details
                  </h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Service Type
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {availableServices.map((type) => (
                        <button
                          key={type.value}
                          onClick={() => setServiceType(type.value as typeof serviceType)}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            serviceType === type.value
                              ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                        >
                          <div className={`text-sm font-medium ${
                            serviceType === type.value
                              ? 'text-orange-700 dark:text-orange-300'
                              : 'text-gray-900 dark:text-gray-100'
                          }`}>
                            {type.label}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {type.description}
                          </div>
                        </button>
                      ))}
                    </div>
                    {destinationType !== 'domestic' && (
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                        <Info className="h-3 w-3" />
                        Evening and Sunday delivery only available for Netherlands
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Scale className="h-4 w-4 inline mr-1" />
                      Package Weight
                    </label>
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        {WEIGHT_PRESETS.map((preset) => (
                          <button
                            key={preset.value}
                            onClick={() => {
                              setWeightGrams(preset.value);
                              setUseCustomWeight(false);
                            }}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              !useCustomWeight && weightGrams === preset.value
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={useCustomWeight}
                            onChange={(e) => setUseCustomWeight(e.target.checked)}
                            className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Custom weight</span>
                        </label>
                        {useCustomWeight && (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={customWeight}
                              onChange={(e) => setCustomWeight(e.target.value)}
                              placeholder="Enter grams"
                              className="w-32 px-3 py-1.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm text-gray-900 dark:text-gray-100"
                            />
                            <span className="text-sm text-gray-500">grams</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <FileText className="h-4 w-4 inline mr-1" />
                      Label Format
                    </label>
                    <div className="flex gap-2">
                      {LABEL_FORMATS.map((format) => (
                        <button
                          key={format.value}
                          onClick={() => setLabelFormat(format.value)}
                          className={`flex-1 p-3 rounded-lg border text-left transition-all ${
                            labelFormat === format.value
                              ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                          }`}
                        >
                          <div className={`text-sm font-medium ${
                            labelFormat === format.value
                              ? 'text-orange-700 dark:text-orange-300'
                              : 'text-gray-900 dark:text-gray-100'
                          }`}>
                            {format.label}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {format.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
              >
                {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                Advanced Options
              </button>

              {showAdvanced && (
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl space-y-4">
                  <div>
                    <label className="flex items-center gap-2 mb-3">
                      <input
                        type="checkbox"
                        checked={useDimensions}
                        onChange={(e) => setUseDimensions(e.target.checked)}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        <Ruler className="h-4 w-4 inline mr-1" />
                        Specify Package Dimensions
                      </span>
                    </label>
                    {useDimensions && (
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Height (cm)
                          </label>
                          <input
                            type="number"
                            value={dimensions.height || ''}
                            onChange={(e) => setDimensions({ ...dimensions, height: parseInt(e.target.value) || 0 })}
                            placeholder="0"
                            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Width (cm)
                          </label>
                          <input
                            type="number"
                            value={dimensions.width || ''}
                            onChange={(e) => setDimensions({ ...dimensions, width: parseInt(e.target.value) || 0 })}
                            placeholder="0"
                            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            Depth (cm)
                          </label>
                          <input
                            type="number"
                            value={dimensions.depth || ''}
                            onChange={(e) => setDimensions({ ...dimensions, depth: parseInt(e.target.value) || 0 })}
                            placeholder="0"
                            className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex gap-3 flex-shrink-0">
          <Button variant="secondary" onClick={onClose} className="flex-1">
            {success ? 'Close' : 'Cancel'}
          </Button>
          {!success && (
            <Button
              onClick={handleGenerateLabel}
              disabled={!canGenerate}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Truck className="h-4 w-4 mr-2" />
                  Generate Label
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
